import { sumBy } from "lodash";
import React from "react";
import { Virtuoso } from "react-virtuoso";
import { Event, Permission, Purchase } from "types";
import { useInfiniteCollection } from "../../../hooks/useInfiniteCollection";
import { Accordion } from "../../Common/Accordion";
import { PurchaseNoteList } from "../../Common/NoteList";
import { Loader } from "../../Loader";
import { NotesBadge } from "../Common/NotesBadge";
import { AddNoteButton, NotesHeading } from "./NotesComponents";

interface BookingNoteListProps {
    event?: Event;
    onAddNoteClick: (e: any) => void;
}

export const BookingNoteList = ({ event, onAddNoteClick }: BookingNoteListProps) => {
    const items = event?.purchaseItems.map((item) => item.id).join(",");
    const {
        data: purchases,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
    } = useInfiniteCollection<Purchase>(
        "purchases",
        {
            "items[in]": items,
        },
        {
            enabled: items ? items.length > 0 : false,
        },
    );

    const purchasesWithNotes = purchases.filter((purchase) => purchase.notes.length > 0);
    const bookingNoteCount = getBookingNoteCount(purchasesWithNotes);

    return (
        <Accordion>
            <Accordion.Header>
                <NotesHeading heading="Purchase Notes" />
                {bookingNoteCount > 0 ? <NotesBadge noteCount={bookingNoteCount} /> : null}
                <AddNoteButton permission={Permission.ADD_EVENT_NOTE} onClick={onAddNoteClick} />
            </Accordion.Header>

            <Accordion.Body>
                {purchasesWithNotes.length > 0 ? (
                    <>
                        <Virtuoso
                            useWindowScroll
                            style={{ height: 100 }}
                            data={purchasesWithNotes}
                            endReached={async () => {
                                if (hasNextPage) {
                                    fetchNextPage();
                                }
                            }}
                            itemContent={(_, purchase) => {
                                return <PurchaseNoteList purchase={purchase} className="ml-4" />;
                            }}
                        />
                        {isFetchingNextPage ? <Loader /> : null}
                    </>
                ) : (
                    <p>No Purchase Notes</p>
                )}
            </Accordion.Body>
        </Accordion>
    );
};

const getBookingNoteCount = (purchasesWithNotes: Purchase[]) => {
    if (purchasesWithNotes.length > 0) {
        // To be sure get the booking note count from the bookings after they have been fetched
        return sumBy(purchasesWithNotes, (purchase) => purchase.notes.length);
    }

    return 0;
};
